/**
 * Wires vaiu.app platformApi to @vaiu/shell ShellApi — same REST calls the UI uses.
 */
import type { ShellApi, ShellExecutor } from '@vaiu/shell';
import { createShellExecutor } from '@vaiu/shell';
import type { PlatformApi } from '@/services/platformApi';

function normalizeProfile(raw: Record<string, unknown>) {
  const user = (raw.user ?? raw.profile ?? raw) as Record<string, unknown>;
  const balances =
    (user.balances as Record<string, number> | undefined) ??
    (raw.balances as Record<string, number> | undefined) ??
    {};

  return {
    currency: (user.currency ?? raw.currency) as string | undefined,
    balances,
    firstName: (user.firstName ?? user.first_name) as string | undefined,
    lastName: (user.lastName ?? user.last_name) as string | undefined,
    username: (user.username ?? user.userName) as string | undefined,
    email: user.email as string | undefined,
    telephone: (user.telephone ?? user.phone) as string | undefined,
    userId: (user.userId ?? user.id ?? user._id) as string | undefined,
  };
}

function mapContact(c: Record<string, unknown>) {
  const profile = (c.profile ?? c) as Record<string, unknown>;
  const name =
    (profile.displayName as string) ||
    [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
    (profile.name as string);
  return {
    id: (c.userId ?? c.id ?? profile.userId) as string | undefined,
    name,
    username: profile.username as string | undefined,
    email: profile.email as string | undefined,
    role: c.role as string | undefined,
  };
}

export function createVaiuAppShellApi(platformApi: PlatformApi): ShellApi {
  return {
    async getProfile() {
      const raw = await platformApi.getProfile();
      return normalizeProfile(raw as Record<string, unknown>);
    },

    async createToken(params) {
      const result = await platformApi.createToken({
        amount: params.amount,
        currency: params.currency,
        message: params.message,
        fundAsNeeded: params.fundAsNeeded,
      });
      const data = result.tokenObj?.data;
      return {
        key: result.key,
        amount: data?.amount ?? params.amount,
        currency: data?.currency ?? params.currency,
        status: result.tokenObj?.status,
        expiresAt: result.expiresAt,
        recipientDelivery: result.recipientDelivery,
      };
    },

    async redeemToken(tokenKey, amount) {
      const result = await platformApi.tokens.redeemToken(tokenKey, amount);
      return {
        success: result.success ?? true,
        message: result.message,
        amount: result.amount ?? result.redeemedAmount,
        currency: result.currency,
        transactionId: result.transactionId ?? result.transaction?.id,
        newBalance: result.newBalance,
      };
    },

    async getTokenDetails(tokenKey) {
      return platformApi.tokens.getTokenDetails(tokenKey) as Promise<
        Record<string, unknown>
      >;
    },

    async getTransactions(filters) {
      const result = await platformApi.transactions.getTransactions(filters);
      const mapTx = (tx: Record<string, unknown>) => ({
        id: (tx.id ?? tx.transactionId ?? tx._id) as string | undefined,
        type: tx.type as string | undefined,
        status: tx.status as string | undefined,
        amount: tx.amount as number | undefined,
        currency: tx.currency as string | undefined,
        description: (tx.description ?? tx.message) as string | undefined,
        createdAt: (tx.createdAt ?? tx.date) as string | undefined,
        counterpartyName: (tx.counterpartyName ?? tx.counterparty) as
          | string
          | undefined,
      });
      return {
        pending: (result.pending ?? []).map(mapTx),
        completed: (result.completed ?? result.transactions ?? []).map(mapTx),
        transactions: (result.transactions ?? []).map(mapTx),
      };
    },

    async getContacts(options) {
      const contacts = await platformApi.contacts.getContacts(options);
      return contacts.map((c) => mapContact(c as unknown as Record<string, unknown>));
    },

    async searchContacts(term) {
      const contacts = await platformApi.contacts.searchContacts(term);
      return contacts.map((c) => mapContact(c as unknown as Record<string, unknown>));
    },

    async getPaymentMethods() {
      const methods = await platformApi.payments.getPaymentMethods();
      return methods.map((m) => ({
        id: m.id,
        type: m.type,
        last4: m.last4 ?? m.card?.last4,
        brand: m.brand ?? m.card?.brand,
        bankName: m.bank_name ?? m.institution_name,
        isDefault: m.isDefault ?? m.default,
      }));
    },

    async searchMerchants(query) {
      const response = await platformApi.merchant.searchMerchants({
        term: query,
        limit: 10,
      });
      const list = Array.isArray(response)
        ? response
        : (response.merchants ?? []);
      return list.map((m: Record<string, unknown>) => ({
        id: (m.merchantId ?? m.id ?? m._id) as string | undefined,
        name: (m.name ?? m.businessName ?? m.displayName) as string | undefined,
        description: m.description as string | undefined,
        category: (m.category ?? m.primaryCategory) as string | undefined,
      }));
    },

    async getInboxMessages(filters) {
      const result = await platformApi.communication.getInboxMessages(filters);
      return (result.messages ?? []).map((m: Record<string, unknown>) => ({
        id: m.id as string | undefined,
        subject: m.subject as string | undefined,
        merchantName: (m.merchantName ?? m.from) as string | undefined,
        preview: (m.preview ?? m.body ?? m.content) as string | undefined,
        isRead: m.isRead as boolean | undefined,
        createdAt: (m.createdAt ?? m.timestamp) as string | undefined,
      }));
    },

    async getReferralDashboard() {
      return platformApi.referrals.getReferralDashboard() as Promise<
        Record<string, unknown>
      >;
    },

    async getSubscriptions() {
      const result = await platformApi.communication.getSubscriptions();
      return result.merchants ?? [];
    },
  };
}

export function createVaiuAppShellExecutor(
  platformApi: PlatformApi,
  getDefaultCurrency?: () => string
): ShellExecutor {
  return createShellExecutor({
    api: createVaiuAppShellApi(platformApi),
    getDefaultCurrency,
  });
}
